<template>
  <div v-if="item">
    <div v-if="isPackLockedByAncestor && lockedByAncestorInfo" class="alert alert-warning d-flex align-items-center mb-3 py-2 px-3">
      <i class="fad fa-lock me-2 fs-3"></i>
      <div class="fs-7">
        Verrouillé par <strong>{{ lockedByAncestorInfo.name }}</strong>
        <span v-if="lockedByAncestorInfo.category" class="text-muted"> · {{ lockedByAncestorInfo.category }}</span>
      </div>
    </div>

    <div class="d-flex align-items-center" style="gap:10px">
      <select @change="onCategoryChanged" class="form-control form-select flex-grow-1" v-model.trim="value" :disabled="isDisabled || isPackLockedByAncestor">
        <option :value="null">{{ trans('Séléctionnez une option') }}</option>
        <option :key="category.id" :value="category.slug.toUpperCase()" v-for="category in branchCategories">
          {{ trans(category.label) }}
        </option>
      </select>
      <label v-if="value && packRecipientsBreakdown && !isPackLockedByAncestor" class="form-check form-switch form-check-custom form-check-solid cursor-pointer mb-0" v-b-tooltip.hover title="Empêcher les descendants de modifier leur configuration">
        <input class="form-check-input" type="checkbox" :checked="!!item.properties.packLocked" @change="togglePackLock">
        <span class="form-check-label fs-7 ms-2 text-nowrap">
          <i :class="item.properties.packLocked ? 'fad fa-lock text-danger' : 'fad fa-lock-open text-muted'" class="me-1"></i>
          {{ item.properties.packLocked ? 'Verrouillé' : 'Verrouiller' }}
        </span>
      </label>
    </div>

    <div v-if="value" class="mt-3">
      <div>
        <span class="badge badge-light-success fs-7 cursor-pointer" @click="showInsurers = !showInsurers">
          <i class="fad fa-shield-check me-1"></i>
          {{ compatibleInsurers.length }} assureur{{ compatibleInsurers.length > 1 ? 's' : '' }} compatible{{ compatibleInsurers.length > 1 ? 's' : '' }}
          <i :class="showInsurers ? 'fad fa-chevron-up' : 'fad fa-chevron-down'" class="ms-1"></i>
        </span>
      </div>
      <div v-if="showInsurers && compatibleInsurers.length > 0" class="mt-2 d-flex flex-wrap" style="gap:4px">
        <span v-for="ins in compatibleInsurers" :key="'pc_ins_' + ins.id" class="badge badge-light-primary fs-8">{{ ins.label }}</span>
      </div>

      <div class="mt-3">
        <button type="button" class="btn btn-light-primary" @click="showAdvanced = !showAdvanced">
          <i :class="showAdvanced ? 'fad fa-chevron-up' : 'fad fa-cog'" class="me-2"></i>
          Options avancées
          <i :class="showAdvanced ? 'fad fa-chevron-up' : 'fad fa-chevron-down'" class="ms-2"></i>
        </button>
      </div>

      <div v-if="showAdvanced" class="mt-2 border rounded p-3" :class="{'opacity-50': isPackLockedByAncestor}">
        <label class="fw-bolder fs-7 mb-2 d-block">Garanties</label>
        <div style="max-height:300px;overflow:auto">
          <div v-for="guarantee in availableGuarantees" :key="'pc_g_' + guarantee.id" class="d-flex align-items-center py-2 border-bottom">
            <div class="flex-grow-1 fs-7">
              <i v-if="guarantee.icon" :class="guarantee.icon + ' me-2'"></i>
              <span class="fw-bolder">{{ guarantee.label }}</span>
            </div>
            <div class="d-flex" style="gap:6px">
              <button type="button" class="btn btn-sm" :class="[isRequiredGuarantee(guarantee.id) ? 'btn-success' : 'btn-light-success', {'pe-none': isPackLockedByAncestor}]" @click="toggleGuarantee(guarantee.id, 'required')">
                <i class="fad fa-shield-check me-1"></i>Obligatoire
              </button>
              <button type="button" class="btn btn-sm" :class="[isOptionalGuarantee(guarantee.id) ? 'btn-warning' : 'btn-light-warning', {'pe-none': isPackLockedByAncestor}]" @click="toggleGuarantee(guarantee.id, 'optional')">
                <i class="fad fa-shield-plus me-1"></i>Optionnelle
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="packRecipientsBreakdown && !isPackLockedByAncestor" class="mt-3">
        <button type="button" class="btn btn-primary btn-sm" @click="applyToDescendants">
          <i class="fad fa-arrow-down-to-bracket me-2"></i>
          Appliquer à {{ packRecipientsBreakdown }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue} from 'vue-property-decorator';
import {getItemModel} from "@/services/ItemFunction";

@Component({})
export default class PropertyPackCategory extends Vue {
  @Prop() itemUuid!: string
  @Prop() propertyCode!: string
  @Prop({default: false}) isDisabled!: boolean

  showAdvanced = false
  showInsurers = false

  get item() {
    return this.$store.getters["contractStore/getEntity"]('items', this.itemUuid)
  }

  get branchCategories(): any[] {
    const branch = this.$store.state.contractStore.branch
    const all = this.$store.state.contractStore.packCategories
    const arr = Array.isArray(all) ? all : Object.values(all || {})
    if (!branch?.packCategories || !Array.isArray(branch.packCategories) || branch.packCategories.length === 0) return arr
    const branchCatIds = branch.packCategories.map((id: any) => Number(id))
    return arr.filter((c: any) => branchCatIds.includes(Number(c.id)))
  }

  get availableGuarantees(): any[] {
    const all = this.$store.state.contractStore.guarantees
    return Array.isArray(all) ? all : Object.values(all || {})
  }

  get value() {
    let v = this.item?.properties?.[this.propertyCode]
    // Fallback sur la valeur par défaut du modèle si pas définie dans item.properties
    if (!v && this.item) {
      const itemType = this.$store.state.contractStore.itemTypes?.[this.item.modelCode]
      const propDecl: any = itemType?.properties ? (Array.isArray(itemType.properties) ? itemType.properties.find((p: any) => p.code === this.propertyCode) : itemType.properties[this.propertyCode]) : null
      if (propDecl?.value) v = propDecl.value
    }
    return v && typeof v === 'string' ? v.toUpperCase() : v
  }

  set value(v: any) {
    if (this.item) {
      Vue.set(this.item.properties, this.propertyCode, v)
    }
  }

  isRequiredGuarantee(id: number): boolean {
    return (this.item?.properties?.requiredGuarantees || []).includes(id)
  }

  isOptionalGuarantee(id: number): boolean {
    return (this.item?.properties?.optionalGuarantees || []).includes(id)
  }

  togglePackLock() {
    if (!this.item || this.isPackLockedByAncestor) return
    const newLocked = !this.item.properties.packLocked
    // Si pas de valeur dans item.properties mais valeur par défaut dans le modèle, la persister
    if (newLocked && !this.item.properties[this.propertyCode]) {
      const itemType = this.$store.state.contractStore.itemTypes?.[this.item.modelCode]
      const propDecl: any = itemType?.properties ? (Array.isArray(itemType.properties) ? itemType.properties.find((p: any) => p.code === this.propertyCode) : itemType.properties[this.propertyCode]) : null
      const defaultValue = propDecl?.value
      if (defaultValue) {
        Vue.set(this.item.properties, this.propertyCode, typeof defaultValue === 'string' ? defaultValue.toUpperCase() : defaultValue)
      } else {
        return
      }
    }
    Vue.set(this.item.properties, 'packLocked', newLocked)
    if (newLocked) {
      this.applyToDescendants()
    } else {
      this.emitUpdate()
    }
  }

  get isPackLockedByAncestor(): boolean {
    let parentUuid = this.item?.parentItem
    while (parentUuid) {
      const parent = this.$store.getters["contractStore/getEntity"]('items', parentUuid)
      if (!parent) break
      if (parent.properties?.packLocked === true) return true
      parentUuid = parent.parentItem
    }
    return false
  }

  get lockedByAncestorInfo(): {name: string, category: string} | null {
    let parentUuid = this.item?.parentItem
    while (parentUuid) {
      const parent = this.$store.getters["contractStore/getEntity"]('items', parentUuid)
      if (!parent) break
      if (parent.properties?.packLocked === true) {
        const parentModel = getItemModel(parent)
        const cat = parent.properties?.packCategory
        return {
          name: parentModel?.name || parent.name || 'parent',
          category: this.getPackCategoryLabel(cat),
        }
      }
      parentUuid = parent.parentItem
    }
    return null
  }

  getPackCategoryLabel(slug: string): string {
    if (!slug) return ''
    const cat = this.branchCategories.find((c: any) => c.slug?.toUpperCase() === slug?.toUpperCase())
    return cat?.label || slug
  }

  toggleGuarantee(id: number, type: 'required' | 'optional') {
    if (!this.item || this.isPackLockedByAncestor) return
    if (!this.item.properties.requiredGuarantees) Vue.set(this.item.properties, 'requiredGuarantees', [])
    if (!this.item.properties.optionalGuarantees) Vue.set(this.item.properties, 'optionalGuarantees', [])
    const req = this.item.properties.requiredGuarantees as number[]
    const opt = this.item.properties.optionalGuarantees as number[]
    if (type === 'required') {
      const idx = req.indexOf(id)
      if (idx !== -1) { req.splice(idx, 1) } else { req.push(id); const oi = opt.indexOf(id); if (oi !== -1) opt.splice(oi, 1) }
    } else {
      const idx = opt.indexOf(id)
      if (idx !== -1) { opt.splice(idx, 1) } else { opt.push(id); const ri = req.indexOf(id); if (ri !== -1) req.splice(ri, 1) }
    }
    // Si l'item est verrouillé par lui-même, re-propager pour maintenir la cohérence avec les descendants gelés
    if (this.item.properties.packLocked) {
      this.applyToDescendants()
    } else {
      this.emitUpdate()
    }
  }

  get compatibleInsurers(): any[] {
    const category = this.item?.properties?.[this.propertyCode]
    if (!category) return []
    const reqGuarantees: number[] = this.item?.properties?.requiredGuarantees || []
    const insurerMap = this.$store.state.contractStore.insurers || {}
    const packs = this.$store.state.contractStore.packs || []
    return (Object.values(insurerMap) as any[]).filter((ins: any) => {
      if (!ins.packs || ins.packs.length === 0) return false
      const pack = packs.find((p: any) =>
        (p.insurer?.slug === ins.slug || p.insurer?.id === ins.id) &&
        p.category?.slug?.toUpperCase() === (typeof category === 'string' ? category.toUpperCase() : '')
      )
      if (!pack) return false
      if (reqGuarantees.length === 0) return true
      const packGuaranteeIds = (pack.packGuarantees || []).map((pg: any) => pg.guarantee?.id)
      return reqGuarantees.every((gId: number) => packGuaranteeIds.includes(gId))
    })
  }

  getPackRecipients(): {uuid: string, modelName: string}[] {
    const result: {uuid: string, modelName: string}[] = []
    if (!this.item) return result
    const walk = (parentUuid: string) => {
      const parent = this.$store.getters["contractStore/getEntity"]('items', parentUuid)
      if (!parent) return
      const model = getItemModel(parent)
      if (!model?.properties) return
      Object.values(model.properties).forEach((pm: any) => {
        const fmt = pm.type?.format || pm.type?.code || ''
        if (!fmt.match(/OBJECT/)) return
        const val = parent.properties?.[pm.code]
        const children = Array.isArray(val) ? val : (val ? [val] : [])
        children.forEach((uuid: string) => {
          const child = this.$store.getters["contractStore/getEntity"]('items', uuid)
          if (!child) return
          const childModel = getItemModel(child)
          const props = childModel?.properties ? (Array.isArray(childModel.properties) ? childModel.properties : Object.values(childModel.properties)) : []
          // Récipient = déclare une propriété de type PACK_CATEGORY OU est un Asset
          const canReceive = props.some((p: any) => p.type?.code === 'PACK_CATEGORY' || p.code === 'packCategory') || childModel?.className === 'Asset'
          if (canReceive) {
            result.push({uuid, modelName: childModel?.name || 'élément'})
          }
          walk(uuid)
        })
      })
    }
    walk(this.item.uuid)
    return result
  }

  get packRecipientsBreakdown(): string {
    const recipients = this.getPackRecipients()
    if (recipients.length === 0) return ''
    const counts: Record<string, number> = {}
    recipients.forEach(r => {
      counts[r.modelName] = (counts[r.modelName] || 0) + 1
    })
    return Object.entries(counts)
      .map(([name, n]) => `${n} ${name}${n > 1 ? 's' : ''}`)
      .join(' + ')
  }

  applyToDescendants() {
    if (!this.item || this.isPackLockedByAncestor) return
    const category = this.item.properties[this.propertyCode]
    const reqG = this.item.properties.requiredGuarantees || []
    const optG = this.item.properties.optionalGuarantees || []
    const recipients = this.getPackRecipients()
    recipients.forEach(({uuid}) => {
      const child = this.$store.getters["contractStore/getEntity"]('items', uuid)
      if (!child) return
      // Trouver le code de la propriété PACK_CATEGORY sur l'itemType du descendant
      // (peut être nommée différemment selon les itemTypes : packCategory, categorie_d_offre, etc.)
      const childModel = this.$store.state.contractStore.itemTypes?.[child.modelCode]
      let childPackCode: string | null = null
      if (childModel?.properties) {
        const props = Array.isArray(childModel.properties) ? childModel.properties : Object.values(childModel.properties)
        const pm: any = props.find((p: any) => p.type?.code === 'PACK_CATEGORY' || p.code === 'packCategory')
        childPackCode = pm?.code || null
      }
      // Fallback : utiliser packCategory pour les Asset legacy sans déclaration explicite
      const targetKey = childPackCode || 'packCategory'
      Vue.set(child.properties, targetKey, category)
      Vue.set(child.properties, 'requiredGuarantees', [...reqG])
      Vue.set(child.properties, 'optionalGuarantees', [...optG])
    })
    this.emitUpdate()
  }

  emitUpdate() {
    this.$emit('propertyUpdated', {code: this.propertyCode, itemUuid: this.itemUuid})
  }

  onCategoryChanged() {
    // Si l'item est verrouillé par lui-même, propager le changement de catégorie aux descendants
    if (this.item?.properties?.packLocked) {
      this.applyToDescendants()
    } else {
      this.emitUpdate()
    }
  }
}
</script>

<style scoped>
</style>
